"use client";

import { useMemo } from "react";

import type { QRProps } from "@/contexts/QREditorContext/types";
import type { VcardData } from "@/types/qr";
import { buildGoogleMapsAddressURL } from "@/utils/googleMaps";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { UseVcardDataResult, VcardTemplateTranslations } from "../types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";

const DEFAULT_VCARD_DATA: VcardData = {
  fullName: undefined,
  email: undefined,
  mobilePhone: undefined,
  landlinePhone: undefined,
  fax: undefined,
  website: undefined,
  companyName: undefined,
  profession: undefined,
  logoUrl: undefined,
  addressObj: {
    address: "",
    numeration: "",
    city: "",
    zipCode: "",
    state: "",
    country: "",
    fullAddress: "",
  },
};

/**
 * Resolves vcard preview data from props with QREditorContext fallback, address formatting,
 * and the payload for generateVCard download.
 */
export const useVcardData = (props: QRProps<"vcard">): UseVcardDataResult => {
  const { data, isInitPreview: isInitialPreview } = usePreviewData(props);

  const translations = props.previewTranslations?.["vcard"] as VcardTemplateTranslations;

  const { address, fullAddress, addressMapsURL, vcardFileData } = useMemo(() => {
    if (!data) {
      return {
        address: "",
        fullAddress: "",
        addressMapsURL: buildGoogleMapsAddressURL(""),
        vcardFileData: DEFAULT_VCARD_DATA,
      };
    }

    const { address: addressValue, numeration, zipCode, city, state, country } = data;
    const fullAddressString = !isInitialPreview
      ? getFullAddress(addressValue, numeration, city, zipCode, state, country)
      : data.address;
    const resolvedFullAddress = (fullAddressString ?? "").trim();

    const vcard: VcardData = {
      logoUrl: data.logoUrl,
      fullName: data.fullName,
      email: data.email,
      mobilePhone: data.mobilePhone,
      landlinePhone: data.landlinePhone,
      fax: data.fax,
      website: data.website,
      companyName: data.companyName,
      profession: data.profession,
      addressObj: {
        address: addressValue,
        numeration,
        city,
        zipCode,
        state,
        country,
        fullAddress: resolvedFullAddress,
      },
    };

    return {
      address: addressValue,
      fullAddress: resolvedFullAddress,
      addressMapsURL: buildGoogleMapsAddressURL(resolvedFullAddress),
      vcardFileData: vcard,
    };
  }, [data, isInitialPreview]);

  return {
    translations,
    isInitialPreview,
    data,
    address,
    fullAddress,
    addressMapsURL,
    vcardFileData,
  };
};
